import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import cn from "classnames";
import styles from "./MessageThread.module.sass";
import Message from "./Message";
import MessageInput from "./MessageInput";
import DeclineModal from "./DeclineModal";
import Icon from "../../Icon";
import ProfileModal from "../ProfileModal";
import { useChannel, useTypingIndicator } from "../../../hooks/useStreamChat";
import { useChatContext } from "../../../contexts/ChatContext";
import streamChatService from "../../../services/streamChat";
import { getAuthToken } from "../../../utils/authHelpers";
import { getDeclineTemplate } from "../../../utils/collabDecline";
import CollabPersonaModal from "../../CollabPersonaModal";
import { usePersona } from "../../../contexts/PersonaContext";

const MessageThread = ({ 
    conversation,
    messages: mockMessages,
    currentUserId,
    onSendMessage,
    onLoadMore,
    isLoading: externalLoading,
    hasMore,
    isUsingStream,
    channelId,
    className,
    collabRequestContext,
    onCollabRequestSent
}) => {
    const { client } = useChatContext();
    // Use Stream hook if connected, otherwise use props
    const {
        messages: streamMessages,
        isLoading: streamLoading,
        typingUsers,
        sendMessage: streamSendMessage,
        sendTypingEvent,
        deleteMessage: streamDeleteMessage,
        updateMessage: streamUpdateMessage,
        loadMoreMessages
    } = useChannel(isUsingStream ? channelId : null);

    const messages = isUsingStream ? streamMessages : mockMessages;
    const isLoading = isUsingStream ? streamLoading : externalLoading;
    
    const [replyingTo, setReplyingTo] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [prefillText, setPrefillText] = useState("");
    const [isSendingCollabRequest, setIsSendingCollabRequest] = useState(false);
    const [collabRequestSent, setCollabRequestSent] = useState(false);
    const [activeCollab, setActiveCollab] = useState(null);
    // Apply-gate state: the most recent collaboration_requests row tied to
    // this Stream channel. We use this to show the "Start collab" promotion
    // banner when the thread is in 'chatting' status (post-approval but
    // pre-workspace), and to hide the legacy compose UI when it doesn't apply.
    const [channelCollabRequest, setChannelCollabRequest] = useState(null);
    const [isPromotingChatToCollab, setIsPromotingChatToCollab] = useState(false);
    // Merged host+creator accounts pick which side of the collab they're on
    // before a collab is created or accepted. `personaPickerAction` holds the
    // deferred action ({ type, args }) while the picker modal is open.
    const { roles: personaRoles, activePersona } = usePersona();
    const isDualPersona = personaRoles.includes('creator') && personaRoles.includes('host');
    const [personaPickerAction, setPersonaPickerAction] = useState(null);
    const defaultPersona = activePersona === 'creator' || activePersona === 'host' ? activePersona : null;
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [isNearBottom, setIsNearBottom] = useState(true);
    
    // Typing indicator hook
    const handleTyping = useTypingIndicator(channelId, sendTypingEvent);

    // Fetch active collaboration for this channel
    useEffect(() => {
        const fetchActiveCollab = async () => {
            if (!channelId) return;
            
            const token = getAuthToken();
            if (!token) return;

            try {
                const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
                const response = await fetch(`${apiBase}/api/collab/workspace?channelId=${encodeURIComponent(channelId)}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
                
                const data = await response.json();
                if (data.ok && data.collaboration && data.collaboration.status === 'active') {
                    setActiveCollab(data.collaboration);
                } else {
                    setActiveCollab(null);
                }
            } catch (err) {
                // Silently fail - no active collab
                setActiveCollab(null);
            }
        };

        fetchActiveCollab();
    }, [channelId]);

    // Fetch the most recent collab request for this channel (apply-gate
    // state machine). If it's 'chatting', we'll show a "Start collab"
    // banner that promotes the chat into a full workspace using the
    // existing accept endpoint (mode: workspace).
    useEffect(() => {
        const fetchChannelRequest = async () => {
            if (!channelId) {
                setChannelCollabRequest(null);
                return;
            }
            const token = getAuthToken();
            if (!token) return;
            try {
                const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
                const res = await fetch(
                    `${apiBase}/api/collab/requests/by-channel?channelId=${encodeURIComponent(channelId)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                if (data?.ok) setChannelCollabRequest(data.request || null);
                else setChannelCollabRequest(null);
            } catch (_) {
                setChannelCollabRequest(null);
            }
        };
        fetchChannelRequest();
    }, [channelId, activeCollab]);
    
    // Compute the "other user" in a DM conversation for profile viewing
    const otherUser = useMemo(() => {
        if (!conversation || conversation.isGroup) return null;
        
        // Get current user ID
        const resolvedCurrentUserId = currentUserId || streamChatService.currentUserId || client?.userID;
        if (!resolvedCurrentUserId) return null;
        
        // Find the member who is not the current user
        const otherMember = conversation.members?.find(m => m.id !== resolvedCurrentUserId);
        if (!otherMember) return null;
        
        // Extract userType from Stream ID prefix (e.g., "host-uuid" or "creator-uuid")
        let userType = 'user';
        const memberId = otherMember.id || '';
        if (memberId.startsWith('host-')) {
            userType = 'host';
        } else if (memberId.startsWith('creator-')) {
            userType = 'creator';
        } else if (otherMember.custom?.userType) {
            userType = otherMember.custom.userType;
        }
        
        // Extract handle (sanitize leading @)
        const handle = conversation.handle?.replace(/^@/, '') || 
                       otherMember.handle?.replace(/^@/, '') || 
                       otherMember.custom?.handle?.replace(/^@/, '') || 
                       null;
        
        return {
            id: memberId,
            userType,
            handle,
            displayName: conversation.first_name || conversation.name || otherMember.name || 'User',
            image: conversation.avatar || otherMember.image || '/images/content/avatar-1.jpg'
        };
    }, [conversation, currentUserId, client?.userID]);
    
    const scrollToBottom = (smooth = true) => {
        messagesEndRef.current?.scrollIntoView({ 
            behavior: smooth ? 'smooth' : 'auto' 
        });
    };
    
    useEffect(() => {
        if (isNearBottom) {
            scrollToBottom();
        }
    }, [messages, isNearBottom]);
    
    const handleScroll = () => {
        if (!scrollContainerRef.current) return;
        
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        setIsNearBottom(distanceFromBottom < 100);
        
        // Load more messages when scrolling to top
        if (scrollTop === 0 && hasMore && !isLoading) {
            if (isUsingStream) {
                loadMoreMessages?.();
            } else {
                onLoadMore?.();
            }
        }
    };
    
    const handleReply = (message) => {
        setReplyingTo(message);
        setEditingMessage(null);
    };
    
    const handleEdit = (message) => {
        setEditingMessage(message);
        setReplyingTo(null);
    };
    
    const handleDelete = async (message) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
            if (isUsingStream) {
                await streamDeleteMessage(message.id);
            } else {
                // Handle mock delete through parent
                console.log('Delete message:', message.id);
            }
        }
    };
    
    const handleDecline = () => {
        setShowDeclineModal(true);
    };
    
    const handleDeclineSend = async (text) => {
        // Send the decline message immediately
        if (isUsingStream) {
            await streamSendMessage(text, []);
        } else {
            onSendMessage({ text, attachments: [] });
        }
    };
    
    const handleDeclineEdit = (text) => {
        // Prefill the composer with the decline text
        setPrefillText(text);
    };
    
    const handlePrefillConsumed = () => {
        // Clear prefill after it's been applied to the composer
        setPrefillText("");
    };
    
    const handleSendMessage = async ({ text, attachments }) => {
        if (isUsingStream) {
            if (editingMessage) {
                await streamUpdateMessage(editingMessage.id, text);
                setEditingMessage(null);
            } else {
                await streamSendMessage(text, attachments);
                setReplyingTo(null);
            }
        } else {
            // Use parent handler for mock data
            onSendMessage({
                text,
                attachments,
                replyTo: replyingTo?.id,
                isEdit: !!editingMessage,
                messageId: editingMessage?.id
            });
            setReplyingTo(null);
            setEditingMessage(null);
        }
    };
    
    const handleInputChange = () => {
        if (isUsingStream && handleTyping) {
            handleTyping();
        }
    };

    // Handle accepting a collab request
    const handleAcceptCollab = useCallback(async (collabRequestId, message, persona = null) => {
        const token = getAuthToken();
        if (!token) {
            console.error('No auth token for accept collab');
            return;
        }

        // Both-roles accounts choose which account the collab is for first.
        if (isDualPersona && !persona) {
            setPersonaPickerAction({ type: 'accept', args: [collabRequestId, message] });
            return;
        }
        const acceptPersona = persona || defaultPersona;

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/requests/accept`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    requestId: collabRequestId,
                    ...(acceptPersona ? { persona: acceptPersona } : {}),
                }),
            });

            const data = await response.json();

            if (data.ok) {
                // Send a system-like message to confirm acceptance with accept flag
                if (isUsingStream) {
                    await streamSendMessage("I've accepted the collaboration request! Let's get started.", [], {
                        collabAcceptResponse: true
                    });
                }
                // Navigate to the collaboration workspace
                window.location.href = `/collabs/workspace?id=${data.collaborationId}`;
            } else {
                console.error('Failed to accept collab:', data.error);
                alert('Failed to accept collaboration: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error accepting collab:', error);
            alert('Failed to accept collaboration. Please try again.');
        }
    }, [isUsingStream, streamSendMessage, isDualPersona, defaultPersona]);

    // Promote a 'chatting' request to a full collaboration workspace.
    // Reuses the workspace-mode accept endpoint — the same code path the
    // ApplicantCard's "Start collab" button hits, so the two surfaces stay
    // in lockstep.
    const handlePromoteToCollab = useCallback(async (maybePersona = null) => {
        // The banner's onClick passes the click event; only honor real personas.
        const persona = maybePersona === 'creator' || maybePersona === 'host' ? maybePersona : null;
        if (isPromotingChatToCollab) return;
        const request = channelCollabRequest;
        if (!request || request.status !== 'chatting') return;

        const token = getAuthToken();
        if (!token) return;

        // Both-roles accounts choose which account the collab is for first.
        if (isDualPersona && !persona) {
            setPersonaPickerAction({ type: 'promote', args: [] });
            return;
        }
        const acceptPersona = persona || defaultPersona;

        setIsPromotingChatToCollab(true);
        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const res = await fetch(`${apiBase}/api/collab/requests/accept`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    requestId: request.id,
                    mode: 'workspace',
                    ...(acceptPersona ? { persona: acceptPersona } : {}),
                }),
            });
            const data = await res.json();
            if (!data?.ok) {
                alert('Failed to start collaboration: ' + (data?.error || 'Unknown error'));
                return;
            }
            window.location.href = `/collabs/workspace?id=${data.collaborationId}`;
        } catch (err) {
            console.error('Error promoting chat to collab:', err);
            alert('Failed to start collaboration.');
        } finally {
            setIsPromotingChatToCollab(false);
        }
    }, [channelCollabRequest, isPromotingChatToCollab, isDualPersona, defaultPersona]);

    // Handle declining a collab request (explicit card, not keyword-based)
    const handleDeclineCollab = useCallback(async (collabRequestId, message) => {
        const token = getAuthToken();
        if (!token) {
            console.error('No auth token for decline collab');
            return;
        }

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiBase}/api/collab/requests/decline`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ requestId: collabRequestId }),
            });

            const data = await response.json();

            if (data.ok) {
                // Auto-send decline message with flag to suppress legacy decline CTA
                // Use the short template to avoid collab-keyword matching
                const declineText = getDeclineTemplate('short');
                if (isUsingStream && channelId) {
                    await streamChatService.sendMessage(channelId, {
                        text: declineText,
                        collabDeclineResponse: true,
                    });
                }
            } else {
                console.error('Failed to decline collab:', data.error);
                alert('Failed to decline collaboration: ' + (data.error || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error declining collab:', error);
            alert('Failed to decline collaboration. Please try again.');
        }
    }, [isUsingStream, channelId]);

    // Handle sending a collab request from this thread
    const handleSendCollabRequest = useCallback(async (maybePersona = null) => {
        const persona = maybePersona === 'creator' || maybePersona === 'host' ? maybePersona : null;
        if (!collabRequestContext || !isUsingStream || isSendingCollabRequest) return;

        const token = getAuthToken();
        if (!token) {
            console.error('No auth token for sending collab request');
            return;
        }

        // Both-roles accounts choose which account they're collabing as before
        // the request is created — otherwise the collab can end up attributed
        // to the wrong profile (e.g. creator instead of host).
        if (isDualPersona && !persona) {
            setPersonaPickerAction({ type: 'request', args: [] });
            return;
        }
        const requesterPersona = persona || defaultPersona;

        setIsSendingCollabRequest(true);

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            
            // Step 1: Create the collab request in the database
            const createResponse = await fetch(`${apiBase}/api/collab/requests/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    recipientUserId: collabRequestContext.recipientUserId,
                    channelId: channelId,
                    targetKind: collabRequestContext.targetKind,
                    targetId: collabRequestContext.targetId,
                    snapshot: collabRequestContext.snapshot,
                    ...(requesterPersona ? { requesterPersona } : {}),
                }),
            });

            const createData = await createResponse.json();

            if (!createData.ok) {
                console.error('Failed to create collab request:', createData.error);
                alert('Failed to send collaboration request: ' + (createData.error || 'Unknown error'));
                return;
            }

            const collabRequestId = createData.requestId;

            // Step 2: Send the message with the collab request attached
            const messageText = collabRequestContext.title
                ? `I'd love to collaborate with you on "${collabRequestContext.title}"! If you're interested in exploring this opportunity, please accept below. If not, let's try again in the future.`
                : "I'd love to collaborate with you! If you're interested in exploring this opportunity, please accept below. If not, let's try again in the future.";

            const message = await streamChatService.sendMessage(channelId, {
                text: messageText,
                collabRequestId,
                collabRequestSnapshot: collabRequestContext.snapshot,
                collabRequestRecipientId: collabRequestContext.recipientUserId,
                collabTargetKind: collabRequestContext.targetKind,
                collabTargetId: collabRequestContext.targetId,
            });

            // Step 3: Link the message ID back to the request
            if (message?.message?.id) {
                await fetch(`${apiBase}/api/collab/requests/link-message`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        requestId: collabRequestId,
                        requestMessageId: message.message.id,
                    }),
                });
            }

            setCollabRequestSent(true);
            if (onCollabRequestSent) {
                onCollabRequestSent();
            }

        } catch (error) {
            console.error('Error sending collab request:', error);
            alert('Failed to send collaboration request. Please try again.');
        } finally {
            setIsSendingCollabRequest(false);
        }
    }, [collabRequestContext, isUsingStream, isSendingCollabRequest, channelId, onCollabRequestSent, isDualPersona, defaultPersona]);

    // Get the resolved current user ID for passing to Message components
    const resolvedCurrentUserId = useMemo(() => {
        return currentUserId || streamChatService.currentUserId || client?.userID || null;
    }, [currentUserId, client?.userID]);
    
    if (!conversation) {
        return (
            <div className={cn(styles.messageThread, styles.empty, className)}>
                <div className={styles.emptyState}>
                    <Icon name="comment" size={48} />
                    <h3>Select a conversation</h3>
                    <p>Choose a conversation from the list to start messaging</p>
                </div>
            </div>
        );
    }
    
    // Group messages by date
    const groupMessagesByDate = (messages) => {
        const groups = [];
        let currentDate = null;
        
        messages?.forEach(message => {
            const messageDate = new Date(message.timestamp).toDateString();
            
            if (messageDate !== currentDate) {
                currentDate = messageDate;
                groups.push({
                    date: messageDate,
                    messages: [message]
                });
            } else {
                groups[groups.length - 1].messages.push(message);
            }
        });
        
        return groups;
    };
    
    const messageGroups = groupMessagesByDate(messages);
    
    // Format typing indicator
    const getTypingText = () => {
        if (!isUsingStream || !typingUsers || typingUsers.length === 0) {
            return conversation.isTyping ? `${conversation.name} is typing...` : null;
        }
        
        if (typingUsers.length === 1) {
            return `${typingUsers[0].name || 'Someone'} is typing...`;
        } else if (typingUsers.length === 2) {
            return `${typingUsers[0].name} and ${typingUsers[1].name} are typing...`;
        } else {
            return `${typingUsers.length} people are typing...`;
        }
    };
    
    const typingText = getTypingText();
    
    return (
        <div className={cn(styles.messageThread, className)}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    {conversation.isGroup ? (
                        <div className={styles.groupAvatars}>
                            {conversation.avatars?.slice(0, 2).map((avatar, index) => (
                                <img 
                                    key={index}
                                    src={avatar} 
                                    alt="" 
                                    className={styles.groupAvatar}
                                />
                            ))}
                        </div>
                    ) : (
                        <img 
                            src={conversation.avatar || "/images/content/avatar-1.jpg"} 
                            alt={conversation.name}
                            className={styles.avatar}
                        />
                    )}
                    
                    <div className={styles.headerInfo}>
                        {!conversation.isGroup && (conversation.first_name || conversation.handle) ? (
                            <div>
                                <h3 className={styles.name}>
                                    {conversation.first_name ? (
                                        <span style={{ color: '#0f084b' }}>{conversation.first_name}</span>
                                    ) : conversation.handle ? (
                                        <span style={{ color: '#0f084b' }}>@{conversation.handle.replace(/^@/, '')}</span>
                                    ) : (
                                        conversation.name
                                    )}
                                </h3>
                                {conversation.handle && conversation.first_name && (
                                    <div style={{ opacity: 0.7, fontWeight: '400', fontSize: '14px', color: '#666', marginTop: '2px' }}>
                                        @{conversation.handle.replace(/^@/, '')}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <h3 className={styles.name}>{conversation.name}</h3>
                        )}
                        <div className={styles.status}>
                            {conversation.isGroup ? (
                                <span>{conversation.memberCount} members</span>
                            ) : (
                                <>
                                    <span className={cn(styles.statusDot, {
                                        [styles.online]: conversation.isOnline
                                    })} />
                                    <span>{conversation.isOnline ? 'Active now' : 'Offline'}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className={styles.headerActions}>
                    {/* View Collab button - only shown when there's an active collaboration */}
                    {activeCollab && (
                        <a 
                            href={`/collabs/workspace?id=${activeCollab.id}`}
                            className={styles.viewCollabBtn}
                        >
                            <Icon name="briefcase" size="16" />
                            View Collab
                        </a>
                    )}
                    
                    {/* View Profile button - only for DMs, not groups */}
                    {!conversation.isGroup && otherUser && (
                        <button 
                            className={styles.viewProfileBtn}
                            onClick={() => setIsProfileModalOpen(true)}
                        >
                            View Profile
                        </button>
                    )}
                </div>
            </div>
            
            {/* Profile Modal */}
            <ProfileModal
                visible={isProfileModalOpen}
                onClose={() => setIsProfileModalOpen(false)}
                otherUser={otherUser}
            />
            
            {/* Messages */}
            <div 
                className={styles.messagesContainer}
                ref={scrollContainerRef}
                onScroll={handleScroll}
            >
                {isLoading && messages.length === 0 && (
                    <div className={styles.loading}>Loading messages...</div>
                )}
                
                {messageGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className={styles.messageGroup}>
                        <div className={styles.dateDivider}>
                            <span>{group.date}</span>
                        </div>
                        
                        {group.messages.map((message, index) => {
                            const prevMessage = group.messages[index - 1];
                            const nextMessage = group.messages[index + 1];
                            // Resolve the current user's Stream ID reliably
                            const resolvedCurrentUserId =
                                currentUserId ||
                                streamChatService.currentUserId ||
                                client?.userID || null;

                            // Support multiple possible shapes from formatter or raw Stream events
                            const messageSenderId =
                                message.sender?.id ||
                                message.user?.id ||
                                message.senderId || null;

                            const isOwn = !!resolvedCurrentUserId && messageSenderId === resolvedCurrentUserId;
                            
                            const showAvatar = (
                                !nextMessage || 
                                nextMessage.sender?.id !== message.sender?.id ||
                                nextMessage.senderId !== message.senderId ||
                                (nextMessage.user?.id !== message.user?.id)
                            );
                            
                            const isFirstInGroup = !prevMessage || 
                                prevMessage.sender?.id !== message.sender?.id ||
                                prevMessage.senderId !== message.senderId;
                            
                            const isLastInGroup = !nextMessage || 
                                nextMessage.sender?.id !== message.sender?.id ||
                                nextMessage.senderId !== message.senderId;
                            
                            return (
                                <Message
                                    key={message.id}
                                    message={message}
                                    isOwn={isOwn}
                                    showAvatar={showAvatar}
                                    isFirstInGroup={isFirstInGroup}
                                    isLastInGroup={isLastInGroup}
                                    onReply={() => handleReply(message)}
                                    onEdit={() => handleEdit(message)}
                                    onDelete={() => handleDelete(message)}
                                    onDecline={() => handleDecline(message)}
                                    onAcceptCollab={handleAcceptCollab}
                                    onDeclineCollab={handleDeclineCollab}
                                    currentUserId={resolvedCurrentUserId}
                                />
                            );
                        })}
                    </div>
                ))}
                
                {/* Typing indicator */}
                {typingText && (
                    <div className={styles.typingIndicator}>
                        <div className={styles.typingDots}>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                        <span className={styles.typingText}>{typingText}</span>
                    </div>
                )}
                
                <div ref={messagesEndRef} />
            </div>
            
            {/* Scroll to bottom button */}
            {!isNearBottom && (
                <button 
                    className={styles.scrollToBottom}
                    onClick={() => scrollToBottom()}
                >
                    <Icon name="arrow-down" />
                </button>
            )}
            
            {/* Apply-gate "Start collab" banner — shown when the host has
                approved a chat (status='chatting') but not yet opened the
                workspace. Same endpoint as the ApplicantCard "Start collab"
                button, so either side can promote. */}
            {channelCollabRequest?.status === 'chatting' && !activeCollab && (
                <ChatToCollabBanner
                    onPromote={handlePromoteToCollab}
                    isBusy={isPromotingChatToCollab}
                />
            )}

            {/* Collab Request Prompt - shown above input when context exists */}
            {collabRequestContext && !collabRequestSent && (
                <div className={styles.collabRequestPrompt}>
                    <div className={styles.collabRequestPromptContent}>
                        <div className={styles.collabRequestPromptIcon}>
                            <Icon name="briefcase" size="20" />
                        </div>
                        <div className={styles.collabRequestPromptText}>
                            <strong>Ready to send a collaboration request</strong>
                            <span>
                                {collabRequestContext.title 
                                    ? `for "${collabRequestContext.title}"`
                                    : 'Click the button to send a formal collab request'}
                            </span>
                        </div>
                    </div>
                    <div className={styles.collabRequestPromptActions}>
                        <button
                            className={styles.collabRequestSendBtn}
                            onClick={handleSendCollabRequest}
                            disabled={isSendingCollabRequest}
                        >
                            <Icon name="send" size="16" />
                            {isSendingCollabRequest ? 'Sending...' : 'Send Collab Request'}
                        </button>
                        <button
                            className={styles.collabRequestCancelBtn}
                            onClick={() => {
                                if (onCollabRequestSent) onCollabRequestSent();
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Success message */}
            {collabRequestSent && (
                <div className={styles.collabRequestSuccess}>
                    <Icon name="check" size="16" />
                    <span>Collaboration request sent! Waiting for response.</span>
                </div>
            )}
            
            {/* Message input */}
            <MessageInput
                onSendMessage={handleSendMessage}
                onTyping={handleInputChange}
                replyingTo={replyingTo}
                editingMessage={editingMessage}
                onCancelReply={() => setReplyingTo(null)}
                onCancelEdit={() => setEditingMessage(null)}
                prefillText={prefillText}
                onPrefillConsumed={handlePrefillConsumed}
            />
            
            {/* Decline collaboration modal */}
            <DeclineModal
                visible={showDeclineModal}
                onClose={() => setShowDeclineModal(false)}
                onSend={handleDeclineSend}
                onEdit={handleDeclineEdit}
            />
            
            {/* Info sidebar */}
            {showInfo && (
                <div className={styles.infoSidebar}>
                    <div className={styles.infoHeader}>
                        <h3>Conversation Info</h3>
                        <button onClick={() => setShowInfo(false)}>
                            <Icon name="close" />
                        </button>
                    </div>
                    
                    <div className={styles.infoContent}>
                        {conversation.isGroup ? (
                            <>
                                <div className={styles.infoSection}>
                                    <h4>Members ({conversation.memberCount})</h4>
                                    {conversation.members?.map(member => (
                                        <div key={member.id} className={styles.member}>
                                            <img src={member.avatar} alt={member.name} />
                                            <span>{member.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.infoSection}>
                                    <img 
                                        src={conversation.avatar} 
                                        alt={conversation.name}
                                        className={styles.infoAvatar}
                                    />
                                    <h3>{conversation.name}</h3>
                                    <p className={styles.infoStatus}>
                                        {conversation.isOnline ? 'Active now' : 'Offline'}
                                    </p>
                                </div>
                            </>
                        )}
                        
                        <div className={styles.infoActions}>
                            <button className={styles.infoAction}>
                                <Icon name="bell" />
                                <span>Mute notifications</span>
                            </button>
                            <button className={styles.infoAction}>
                                <Icon name="search" />
                                <span>Search in conversation</span>
                            </button>
                            <button className={styles.infoAction}>
                                <Icon name="folder" />
                                <span>View files</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CollabPersonaModal
                isOpen={!!personaPickerAction}
                onClose={() => setPersonaPickerAction(null)}
                onSelect={(persona) => {
                    const action = personaPickerAction;
                    setPersonaPickerAction(null);
                    if (!action) return;
                    if (action.type === 'accept') handleAcceptCollab(action.args[0], action.args[1], persona);
                    else if (action.type === 'promote') handlePromoteToCollab(persona);
                    else if (action.type === 'request') handleSendCollabRequest(persona);
                }}
            />
        </div>
    );
};

/**
 * Small inline banner shown when a thread is in 'chatting' status — i.e.
 * the host approved an application for chat only and hasn't yet opened a
 * full collaboration workspace. Either party can hit "Start collab" to
 * promote the chat into a workspace (which seeds default agreements, opens
 * the collab tracker, etc.). Styled inline to avoid a Sass touchup.
 */
const ChatToCollabBanner = ({ onPromote, isBusy }) => (
    <div
        style={{
            margin: '8px 12px 0',
            padding: '12px 14px',
            borderRadius: 12,
            background: 'linear-gradient(135deg, #fdfbf6 0%, #fff 100%)',
            border: '1px solid #f1ead8',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
        }}
    >
        <div style={{ flex: 1, minWidth: 200 }}>
            <div
                style={{
                    fontFamily: "'Sailors', serif",
                    fontSize: 16,
                    color: '#0f084b',
                    lineHeight: 1.2,
                }}
            >
                Ready to make it official?
            </div>
            <div
                style={{
                    marginTop: 4,
                    fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                    fontSize: 12.5,
                    color: '#6b7280',
                    lineHeight: 1.45,
                }}
            >
                Promote this chat into a full collaboration workspace — agreements, deliverables, and tracking.
            </div>
        </div>
        <button
            type="button"
            onClick={onPromote}
            disabled={isBusy}
            style={{
                padding: '10px 18px',
                borderRadius: 100,
                border: '1.5px solid #dc1d08',
                background: isBusy ? '#e89186' : '#dc1d08',
                color: '#ffffff',
                fontFamily: "'Neue Einstellung', 'Poppins', sans-serif",
                fontSize: 13.5,
                fontWeight: 600,
                cursor: isBusy ? 'default' : 'pointer',
            }}
        >
            {isBusy ? 'Starting…' : 'Start collab'}
        </button>
    </div>
);

export default MessageThread;